import React, { ReactElement } from 'react'
import { TabActionPromptContextProvider } from './TabActionPromptContext'
// Sub imports.
import { Tab } from './Types'
import VerticalTabsMenu, { TabItem } from './VerticalTabsMenu'
import { EMPTY_STRING } from '../../Constants/Separators'
// CSS imports. Commented out because of current build process.
// import './tabs.scss';

/**
 * Component properties type.
 */
type Props = {
    tabs: Tab[]
    activeTabID: number
    isVisible?: boolean
}

/**
 * Tabs generic component with menu and content of active tab.
 * @param tabs List of tabs settings.
 * @param activeTabID The currently active tab identifier.
 * @param activateTab Tab activation callback.
 */
// eslint-disable-next-line
const Tabs: React.FC<Props> = ({ tabs, activeTabID, isVisible }: Props): ReactElement => {
    const nonRemovedTabs: Tab[] = tabs.filter(({ isRemoved }: Tab): boolean => !isRemoved)
    const displayedTabs: Tab[] = tabs.filter(
        ({ isDisplayed, isRemoved }: Tab): boolean => (isDisplayed ?? true) && !isRemoved
    )
    const selectableTabs: Tab[] = displayedTabs.filter(({ isDisabled }: Tab): boolean => !isDisabled)

    const isActiveTabDisplayed: boolean = selectableTabs.some(
        (displayedTab: Tab): boolean => displayedTab.ID === activeTabID
    )
    const activeTab: number = isActiveTabDisplayed ? activeTabID : selectableTabs[0]?.ID ?? displayedTabs[0]?.ID ?? 0

    const toTabContent = (tabSettings: Tab, index: number): ReactElement => (
        <div
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            key={`${tabSettings.tabsMenuItem.title?.toString() ?? EMPTY_STRING}-tabs-content-${index}`}
            className={tabSettings.ID === activeTab ? 'tab-content active-tab' : 'tab-content'}
        >
            {tabSettings.content}
        </div>
    )

    const toTabs = (tabsList: Tab[]): ReactElement[] => tabsList.map(toTabContent)

    return (
        <div className='tabs'>
            {displayedTabs && displayedTabs.length > 1 ? (
                <VerticalTabsMenu
                    tabsMenuItems={displayedTabs.map(
                        ({ ID, tabsMenuItem, hasError, isDisabled }: Tab): TabItem => ({
                            ...tabsMenuItem,
                            active: ID === activeTab,
                            isDisabled,
                            hasError
                        })
                    )}
                    isVisible={isVisible ?? true}
                />
            ) : (
                <></>
            )}

            <TabActionPromptContextProvider activeTabID={activeTab}>
                {toTabs(nonRemovedTabs)}
            </TabActionPromptContextProvider>
        </div>
    )
}

export default Tabs
